import { useState } from "react";
import { useAuth } from "../state/auth";
import { Navigate, useLocation } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ShieldCheck, AlertTriangle, Loader2 } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("admin@coastalwildlife.com");
  const [password, setPassword] = useState("ChangeMe123!");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const loc = useLocation();

  if (user) return <Navigate to={loc.state?.from?.pathname || "/"} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try { await login(email, password); }
    catch (ex) { setErr(ex.message || "Invalid credentials"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center app-noise p-6" data-testid="page-login">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl w-full">
        <div className="hidden md:flex flex-col justify-center">
          <div className="flex items-center gap-3 mb-6">
            <div className="size-10 rounded-md bg-[hsl(var(--positive))]/15 border border-[hsl(var(--positive))]/30 grid place-items-center">
              <ShieldCheck size={22} className="text-[hsl(var(--positive))]" />
            </div>
            <div>
              <div className="font-display text-xl font-semibold">Coastal Wildlife</div>
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Command Center</div>
            </div>
          </div>
          <h1 className="font-display text-3xl font-semibold leading-tight">A trust layer between GoHighLevel and FieldRoutes.</h1>
          <p className="text-muted-foreground mt-3 text-sm">Executive dashboards, rep accountability, reconciliation workflow, and Monday morning recap emails — all in one command center.</p>
          <div className="mt-6 space-y-3 text-sm">
            <div className="flex gap-3"><span className="text-[hsl(var(--positive))]">•</span>Revenue + pipeline health at a glance</div>
            <div className="flex gap-3"><span className="text-[hsl(var(--positive))]">•</span>Discrepancy queue between systems</div>
            <div className="flex gap-3"><span className="text-[hsl(var(--positive))]">•</span>Automated daily and weekly recaps via Resend</div>
          </div>
        </div>
        <Card className="bg-[hsl(var(--surface-1))]">
          <CardContent className="p-8">
            <div className="md:hidden flex items-center gap-3 mb-6">
              <ShieldCheck size={22} className="text-[hsl(var(--positive))]" />
              <div className="font-display text-lg font-semibold">Coastal Wildlife CC</div>
            </div>
            <h2 className="font-display text-lg font-semibold">Sign in</h2>
            <p className="text-xs text-muted-foreground mt-1">Internal access only.</p>
            <form onSubmit={onSubmit} className="mt-6 space-y-3">
              <div>
                <Label htmlFor="email" className="text-xs">Email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
              </div>
              <div>
                <Label htmlFor="password" className="text-xs">Password</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
              </div>
              {err && <div className="text-xs text-[hsl(var(--negative))] flex items-center gap-2"><AlertTriangle size={12} /> {err}</div>}
              <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit">
                {busy ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
                Sign in
              </Button>
            </form>
            <div className="mt-6 pt-6 border-t border-border">
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-2">Seeded test accounts</div>
              <div className="text-xs space-y-1 font-mono">
                <div>admin@coastalwildlife.com / ChangeMe123!</div>
                <div>manager@coastalwildlife.com / ChangeMe123!</div>
                <div>rep@coastalwildlife.com / ChangeMe123!</div>
                <div>viewer@coastalwildlife.com / ChangeMe123!</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
